import React, { useState } from 'react';
import { useWallet, useConnection } from '@solana/wallet-adapter-react';
import { LAMPORTS_PER_SOL } from '@solana/web3.js';
import { supabase } from '../supabaseClient';
import { 
  buildDonateInstruction, 
  createTransaction, 
  WALLETS 
} from '../utils/popperverseProgram';
import './Donate.css';

const Donate = () => {
  const { publicKey, sendTransaction } = useWallet();
  const { connection } = useConnection();
  const [donationAmount, setDonationAmount] = useState(0.1);
  const [donationMessage, setDonationMessage] = useState('');
  const [isDonating, setIsDonating] = useState(false);
  const [totalDonated, setTotalDonated] = useState(0);

  // Donation wallet - WALLETS.DONATE now correctly points to SOLFLARE_DONATE
  const DONATION_WALLET = WALLETS.DONATE;

  // Load total donations from localStorage
  React.useEffect(() => {
    const stored = localStorage.getItem('popperverseTotalDonations');
    if (stored) {
      setTotalDonated(parseFloat(stored));
    }
  }, []);

  const handleDonate = async () => {
    if (!publicKey) {
      alert('Please connect your wallet first!');
      return;
    }

    if (donationAmount <= 0) {
      alert('Please enter a valid donation amount!');
      return;
    }

    setIsDonating(true);

    try {
      const amountLamports = Math.floor(donationAmount * LAMPORTS_PER_SOL);
      
      console.log('[Donate] Building donation transaction...');
      console.log('[Donate] Amount:', donationAmount, 'SOL (', amountLamports, 'lamports)');
      console.log('[Donate] Donor:', publicKey.toString());
      console.log('[Donate] Destination:', DONATION_WALLET.toString());
      
      // Build the donate instruction using the Popperverse program
      const donateIx = buildDonateInstruction({
        donor: publicKey,
        donateWallet: DONATION_WALLET,
        amountLamports,
        message: donationMessage.trim() || null, // Optional message (max 200 chars)
      });

      // Create transaction
      const transaction = createTransaction(donateIx);

      const { blockhash } = await connection.getLatestBlockhash();
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = publicKey;

      // Simulate first to catch errors
      console.log('[Donate] Simulating transaction...');
      try {
        const simulation = await connection.simulateTransaction(transaction);
        if (simulation.value.err) {
          console.error('[Donate] Simulation error:', simulation.value.err);
          console.error('[Donate] Simulation logs:', simulation.value.logs);
          throw new Error(`Simulation failed: ${JSON.stringify(simulation.value.err)}\nLogs: ${simulation.value.logs?.join('\n')}`);
        }
        console.log('[Donate] Simulation passed ✓');
      } catch (simError) {
        console.error('[Donate] Simulation exception:', simError);
        throw simError;
      }

      const signature = await sendTransaction(transaction, connection);
      await connection.confirmTransaction(signature, 'confirmed');

      // Calculate charity amount (10%)
      const charityAmount = donationAmount * 0.1;

      // Log to Supabase
      try {
        const { error: dbError } = await supabase
          .from('donations')
          .insert({
            wallet_address: publicKey.toString(),
            donation_amount_sol: donationAmount,
            transaction_signature: signature,
            destination_wallet: DONATION_WALLET.toString(),
            charity_amount_sol: charityAmount
          });

        if (dbError) {
          console.error('Failed to log donation to Supabase:', dbError);
          // Continue anyway - donation transaction succeeded
        } else {
          console.log('Donation successfully logged to Supabase');
        }
      } catch (dbError) {
        console.error('Supabase logging error:', dbError);
        // Continue anyway
      }

      // Update total donations
      const newTotal = totalDonated + donationAmount;
      setTotalDonated(newTotal);
      localStorage.setItem('popperverseTotalDonations', newTotal.toString());

      alert(`Thank you for your donation of ${donationAmount} SOL! 🐱\n10% will go to cat charities.\nTransaction: ${signature}`);
    } catch (error) {
      console.error('Donation error:', error);
      alert(`Donation failed: ${error.message}`);
    } finally {
      setIsDonating(false);
    }
  };

  const charityAmount = totalDonated * 0.1;

  return (
    <div className="donate-page">
      <h2>Support the Popperverse! 🐱</h2>
      <p className="donate-description">
        Your donations help us create more amazing Popper plushies and support cats in need!
      </p>

      <div className="charity-notice">
        <h3>🎗️ 10% Goes to Cat Charities</h3>
        <p>Every donation supports both the Popperverse and helps cats around the world.</p>
      </div>

      <div className="donation-stats">
        <div className="stat-card">
          <h4>Total Donated</h4>
          <p className="stat-value">{totalDonated.toFixed(4)} SOL</p>
        </div>
        <div className="stat-card charity">
          <h4>To Cat Charities</h4>
          <p className="stat-value">{charityAmount.toFixed(4)} SOL</p>
        </div>
      </div>

      <div className="donation-form">
        <h3>Make a Donation</h3>
        
        <div className="amount-selector">
          <button 
            className="amount-btn"
            onClick={() => setDonationAmount(0.1)}
            disabled={isDonating}
          >
            0.1 SOL
          </button>
          <button 
            className="amount-btn"
            onClick={() => setDonationAmount(0.5)}
            disabled={isDonating}
          >
            0.5 SOL
          </button>
          <button 
            className="amount-btn"
            onClick={() => setDonationAmount(1)}
            disabled={isDonating}
          >
            1 SOL
          </button>
          <button 
            className="amount-btn"
            onClick={() => setDonationAmount(5)}
            disabled={isDonating}
          >
            5 SOL
          </button>
        </div>

        <div className="custom-amount-input">
          <label htmlFor="customAmount">Custom Amount (SOL):</label>
          <input
            type="number"
            id="customAmount"
            min="0.01"
            step="0.01"
            value={donationAmount}
            onChange={(e) => setDonationAmount(parseFloat(e.target.value) || 0)}
            className="custom-amount"
            disabled={isDonating}
          />
        </div>

        <div className="message-input">
          <label htmlFor="donationMessage">Optional Message (max 200 chars):</label>
          <textarea
            id="donationMessage"
            maxLength={200}
            value={donationMessage}
            onChange={(e) => setDonationMessage(e.target.value)}
            placeholder="Leave a message with your donation..."
            className="donation-message"
            disabled={isDonating}
            rows={2}
          />
        </div>

        <button
          className="donate-btn"
          onClick={handleDonate}
          disabled={isDonating || !publicKey}
        >
          {isDonating ? 'Processing...' : `Donate ${donationAmount} SOL`}
        </button>

        {!publicKey && (
          <div className="wallet-notice">
            <p>⚠️ Connect your wallet to donate!</p>
          </div>
        )}
      </div>

      <div className="impact-section">
        <h3>Your Impact</h3>
        <div className="impact-grid">
          <div className="impact-card">
            <div className="impact-icon">🎨</div>
            <h4>New Designs</h4>
            <p>Fund creation of new plushie designs and prototypes</p>
          </div>
          <div className="impact-card">
            <div className="impact-icon">🏭</div>
            <h4>Production</h4>
            <p>Help us manufacture more high-quality plushies</p>
          </div>
          <div className="impact-card">
            <div className="impact-icon">🐱</div>
            <h4>Cat Charities</h4>
            <p>Support shelters, rescue organizations, and cat welfare</p>
          </div>
          <div className="impact-card">
            <div className="impact-icon">🌟</div>
            <h4>Community</h4>
            <p>Build the Popperverse community and events</p>
          </div>
        </div>
      </div>

      <div className="featured-charities">
        <h3>Featured Cat Charities</h3>
        <ul>
          <li>🏠 Local cat shelters and rescue organizations</li>
          <li>💉 SOCK FIP - Supporting cats with FIP treatment</li>
          <li>🩺 Every Cat Health Foundation - FIP research and care</li>
          <li>🐱 Sparkle Cat Rescue - Cat rescue and adoption</li>
          <li>🩹 Veterinary care for stray and abandoned cats</li>
          <li>🍽️ Food and supply programs for cats in need</li>
          <li>🐾 TNR (Trap-Neuter-Return) programs</li>
        </ul>
      </div>
    </div>
  );
};

export default Donate;
